import {graphql, useStaticQuery} from 'gatsby'
import {m as motion} from 'motion/react'
import React from 'react'

import Image from '@/components/image'
import {LabelCard} from '@/components/label-card'
import PrismicLink from '@/components/prismic-link'
import {useLocalizedContent} from '@/hooks/use-localized-content'
import {CardsGridWithFilters} from '@/sections/cards-grid-with-filters'

import type {IntegrationsGridSliceFragment, SyncedIntegrationsQuery} from '@/graphql-types'
import type {BackgroundColor} from '@/utils/is-valid-background-color'

export const IntegrationsGridSlice = ({primary, items}: IntegrationsGridSliceFragment) => {
    const {allPrismicSyncedIntegration, prismicSyncedIntegrationsCategories} =
        useStaticQuery<SyncedIntegrationsQuery>(query)

    const content = useLocalizedContent(allPrismicSyncedIntegration.nodes)
    const categories = prismicSyncedIntegrationsCategories.data.categories

    // Validate content and throw error if integrations are missing `endymionApp`
    const missingEndymionApps = content
        .filter(({data: {endymionApp}}) => !endymionApp)
        .map(({data: {name}}) => name)

    if (missingEndymionApps.length > 0) {
        throw new Error(`SlackNotification: Missing Endymion integrations: ${missingEndymionApps}`)
    }

    const {
        title,
        cardCtaText,
        categoryFilterPlaceholder,
        categoryFilterAllLabel,
        showingAllResults,
        showingResultsFor,
        removeFilter,
        noResultsFound,
        searchPlaceholder,
    } = primary

    // Extract featured integration IDs
    const featuredIntegrationIds = items.map(
        ({featured_integration}) => featured_integration.document.id,
    )

    // Map content to integrations with additional metadata
    const integrations = content.map(
        ({id, data: {name, description, endymionApp, cta_link, logo}}) => {
            const category = categories.find(({category}) => category === endymionApp.category)

            return {
                id,
                title: name,
                description,
                category: category?.labelText,
                Image: () => <Image image={logo} />,
                labelColor: (category?.labelColor || 'yellow') as BackgroundColor,
                CTA: (props: React.ComponentProps<typeof Card>) => (
                    <Card
                        {...props}
                        {...cta_link}
                        aria-label={cardCtaText}
                        whileHover={{
                            scale: 1.05,
                        }}
                    />
                ),
            }
        },
    )

    // Sort integrations by featured status
    const sortedIntegrations = integrations.sort((a, b) => {
        const isAFeatured = featuredIntegrationIds.includes(a.id)
        const isBFeatured = featuredIntegrationIds.includes(b.id)

        if (isAFeatured && isBFeatured) {
            // Reverse the order of featured integrations
            return featuredIntegrationIds.indexOf(a.id) - featuredIntegrationIds.indexOf(b.id)
        }

        return Number(isBFeatured) - Number(isAFeatured) // Featured integrations come first
    })

    return (
        <CardsGridWithFilters
            title={title}
            filters={[
                {
                    name: 'category',
                    placeholder: categoryFilterPlaceholder,
                    allLabel: categoryFilterAllLabel,
                },
            ]}
            showingAllResultsLabel={showingAllResults}
            showingResultsForLabel={showingResultsFor}
            clearText={removeFilter}
            noResultsFoundText={noResultsFound}
            searchPlaceholder={searchPlaceholder}
            headLayout="compact"
            cardsLayout="4-element grid"
            batchSize={8}
        >
            {sortedIntegrations.map((integration) => (
                <LabelCard key={integration?.id} {...integration} />
            ))}
        </CardsGridWithFilters>
    )
}

const Card = motion.create(PrismicLink)

export const integrationsGridSliceFragment = graphql`
    fragment IntegrationsGridSlice on PrismicSlicePageDataBodyIntegrationsGrid {
        __typename
        primary {
            title
            cardCtaText: card_cta_text
            categoryFilterPlaceholder: category_filter_placeholder
            categoryFilterAllLabel: category_filter_all_label
            showingAllResults: showing_all_results
            showingResultsFor: showing_results_for
            removeFilter: remove_filter
            noResultsFound: no_results_found
            searchPlaceholder: search_placeholder
        }
        items {
            featured_integration {
                document {
                    ... on PrismicSyncedIntegration {
                        id
                    }
                }
            }
        }
    }
`

export const query = graphql`
    query SyncedIntegrations($imageBreakpoints: [Int], $threeColumns: String) {
        allPrismicSyncedIntegration(sort: {fields: data___name, order: ASC}) {
            nodes {
                _previewable
                lang
                id
                data {
                    name
                    description
                    logo {
                        alt
                        copyright
                        url
                        dimensions {
                            height
                            width
                        }
                        gatsbyImageData(breakpoints: $imageBreakpoints, sizes: $threeColumns)
                    }
                    cta_link {
                        ...PrismicLink
                    }
                    endymionApp: endymion_app {
                        category
                        slug
                    }
                }
            }
        }
        prismicSyncedIntegrationsCategories {
            data {
                categories {
                    category
                    labelText: label_text
                    labelColor: label_color
                }
            }
        }
    }
`
